import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { apiCall } from '../../utils/api';
import { Activity, ChevronDown, AlertTriangle, Loader2 } from 'lucide-react';

/**
 * GitHub-style activity heatmap for audit logs.
 * Props:
 *  - onSelectDate(dateStr): optional. Called when user clicks a cell.
 *  - days: number of days to show (default 90).
 */
export default function AuditHeatmap({ onSelectDate, days = 90 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState(days);
  const [hoverCell, setHoverCell] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiCall(`/api/admin/audit-logs/heatmap?days=${range}`);
      setData(res);
    } catch (e) {
      setError(e?.message || 'Failed to load heatmap');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const series = data?.series || [];
  const maxCount = data?.max_count || 0;

  // Bucket counts into 5 intensity levels (0-4)
  const intensity = (n) => {
    if (!n) return 0;
    if (maxCount <= 4) return n; // raw mapping for tiny datasets
    const ratio = n / maxCount;
    if (ratio <= 0.15) return 1;
    if (ratio <= 0.35) return 2;
    if (ratio <= 0.65) return 3;
    return 4;
  };

  const cellColor = (level) => {
    const palette = [
      'bg-slate-100',                  // 0
      'bg-violet-200',                 // 1
      'bg-violet-400',                 // 2
      'bg-violet-600',                 // 3
      'bg-violet-800',                 // 4
    ];
    return palette[Math.min(level, 4)];
  };

  // Lay out into weekly columns (Sun..Sat)
  const weeks = useMemo(() => {
    if (!series.length) return [];
    const cols = [];
    let col = new Array(7).fill(null);
    series.forEach((day, idx) => {
      const dow = new Date(day.date + 'T00:00:00').getDay(); // 0..6
      if (idx === 0) {
        // pad left so first item lands in its day-of-week row
        for (let i = 0; i < dow; i++) col[i] = { padding: true };
      }
      col[dow] = day;
      if (dow === 6 || idx === series.length - 1) {
        cols.push(col);
        col = new Array(7).fill(null);
      }
    });
    return cols;
  }, [series]);

  // Month labels above the grid
  const monthLabels = useMemo(() => {
    const labels = [];
    let lastMonth = -1;
    weeks.forEach((col, ci) => {
      const firstDay = col.find((c) => c && !c.padding);
      if (!firstDay) {
        labels.push(null);
        return;
      }
      const m = new Date(firstDay.date + 'T00:00:00').getMonth();
      if (m !== lastMonth) {
        labels.push({ col: ci, label: new Date(firstDay.date + 'T00:00:00').toLocaleString(undefined, { month: 'short' }) });
        lastMonth = m;
      } else {
        labels.push(null);
      }
    });
    return labels;
  }, [weeks]);

  const handleClick = (day) => {
    if (!onSelectDate || !day || day.padding) return;
    onSelectDate(day.date);
  };

  const formatTooltipDate = (d) =>
    new Date(d + 'T00:00:00').toLocaleDateString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-5"
      data-testid="audit-heatmap-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-violet-600" />
            Activity heatmap
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Daily event counts across the platform. Click a day to filter the log below.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-500 hidden sm:flex items-center gap-3">
            <span><strong className="text-slate-900">{data?.total ?? 0}</strong> events</span>
            <span><strong className="text-slate-900">{data?.active_days ?? 0}</strong> active days</span>
            <span>peak <strong className="text-slate-900">{data?.max_count ?? 0}</strong>/day</span>
          </div>
          <div className="relative">
            <select
              value={range}
              onChange={(e) => setRange(parseInt(e.target.value, 10))}
              className="appearance-none pl-3 pr-8 py-1.5 border border-slate-300 rounded-lg text-xs bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-400 font-medium cursor-pointer"
              data-testid="audit-heatmap-range"
            >
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
              <option value={365}>Last 365 days</option>
            </select>
            <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-10 text-slate-500 gap-2 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading heatmap…
        </div>
      )}

      {data && (
        <div className="relative overflow-x-auto pb-2">
          {/* Month labels */}
          <div
            className="flex items-end gap-[3px] pl-7 mb-1 text-[10px] font-semibold text-slate-500"
            style={{ minWidth: `${weeks.length * 15 + 28}px` }}
          >
            {monthLabels.map((lbl, i) => (
              <div key={i} style={{ width: '12px' }} className="flex-shrink-0">
                {lbl?.label || ''}
              </div>
            ))}
          </div>

          {/* Grid */}
          <div className="flex gap-[3px]" style={{ minWidth: `${weeks.length * 15 + 28}px` }}>
            {/* Day-of-week labels */}
            <div className="flex flex-col gap-[3px] pr-1 text-[10px] text-slate-400 font-medium select-none">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (
                <div
                  key={d}
                  style={{ height: '12px', lineHeight: '12px' }}
                  className={i % 2 === 1 ? '' : 'opacity-0'}
                >
                  {d}
                </div>
              ))}
            </div>

            {weeks.map((col, ci) => (
              <div key={ci} className="flex flex-col gap-[3px]">
                {col.map((day, ri) => {
                  if (!day || day.padding) {
                    return <div key={ri} style={{ width: '12px', height: '12px' }} />;
                  }
                  const level = intensity(day.count);
                  return (
                    <button
                      key={ri}
                      type="button"
                      onMouseEnter={() => setHoverCell(day)}
                      onMouseLeave={() => setHoverCell(null)}
                      onFocus={() => setHoverCell(day)}
                      onBlur={() => setHoverCell(null)}
                      onClick={() => handleClick(day)}
                      style={{ width: '12px', height: '12px' }}
                      className={`${cellColor(level)} rounded-[2px] hover:ring-2 hover:ring-violet-300 hover:ring-offset-1 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-500`}
                      data-testid={`heatmap-cell-${day.date}`}
                      aria-label={`${day.date}: ${day.count} events`}
                    />
                  );
                })}
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-end gap-1.5 mt-3 text-[10px] text-slate-500">
            <span>Less</span>
            {[0, 1, 2, 3, 4].map((lvl) => (
              <span
                key={lvl}
                className={`${cellColor(lvl)} rounded-[2px]`}
                style={{ width: '11px', height: '11px' }}
              />
            ))}
            <span>More</span>
          </div>

          {/* Hover tooltip — render inline so it doesn't escape the card */}
          {hoverCell && (
            <div
              className="mt-3 px-3 py-2 bg-slate-900 text-white rounded-lg text-xs shadow-lg max-w-md"
              data-testid="heatmap-tooltip"
            >
              <div className="font-semibold mb-1">
                {formatTooltipDate(hoverCell.date)}
                <span className="ml-2 text-violet-300">
                  {hoverCell.count} event{hoverCell.count !== 1 ? 's' : ''}
                </span>
              </div>
              {hoverCell.count > 0 && (
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-slate-300">
                  {Object.entries(hoverCell.by_action || {}).slice(0, 5).map(([k, v]) => (
                    <span key={k}>{k}: <span className="text-white font-medium">{v}</span></span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
